import { useFormatting } from "@msys/ui";
import { TextField } from "@mui/material";
import type { GridRenderEditCellParams } from "@mui/x-data-grid-premium";
import { clamp } from "lodash-es";
import React from "react";

interface DataGridFloatEditCellProps extends GridRenderEditCellParams {
  min?: number;
  max?: number;
  multiplier?: number;
  unit?: string;
}

export const DataGridFloatEditCell = ({
  id,
  field,
  value,
  api,
  min,
  max,
  multiplier = 1,
  unit,
}: DataGridFloatEditCellProps) => {
  const { getEditableFloat, getFloat } = useFormatting();

  const [inputValue, setInputValue] = React.useState<string>(() =>
    getEditableFloat((value ?? 0) * multiplier)
  );

  const commit = React.useCallback(
    (str: string) => {
      let parsed = getFloat(str, false) / multiplier;
      if (Number.isNaN(parsed)) parsed = 0;
      parsed = clamp(
        parsed,
        min ?? Number.MIN_SAFE_INTEGER,
        max ?? Number.MAX_SAFE_INTEGER
      );
      api.setEditCellValue({ id, field, value: parsed });
      api.stopCellEditMode({ id, field });
    },
    [id, field, api, getFloat, multiplier, min, max]
  );

  return (
    <TextField
      value={inputValue}
      onChange={e => setInputValue(e.target.value)}
      onBlur={e => commit(e.target.value)}
      onKeyDown={e => {
        if (e.key === "Enter") commit((e.target as HTMLInputElement).value);
        else if (e.key === "Escape")
          api.stopCellEditMode({ id, field, ignoreModifications: true });
      }}
      autoFocus
      size="extra-small"
      fullWidth
      variant="outlined"
      sx={{ "& fieldset": { border: "none" } }}
      slotProps={
        unit ? { input: { endAdornment: <span>{unit}</span> } } : undefined
      }
    />
  );
};
